import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import LandingNavbar from '../../components/LandingNavbar';
import LandingFooter from '../../components/LandingFooter';
import { HELP_CATEGORIES, getArticlesByCategory, getAllArticles, getCategoryById } from '../../data/helpArticles';
import './Help.css';

/**
 * Help Center Home Page
 * Shows all help categories for users to browse
 * Logged in: uses app Layout | Not logged in: uses LandingNavbar/Footer
 * URL: /help
 */
const HelpHome = () => {
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  // Popular articles for quick access
  const popularArticles = [
    { category: 'getting-started', slug: 'app-kaise-shuru-karein', title: 'App Kaise Shuru Karein' },
    { category: 'transactions', slug: 'expense-kaise-add-karein', title: 'Expense Kaise Add Karein' },
    { category: 'budgets', slug: 'budget-kaise-banayein', title: 'Monthly Budget Kaise Set Karein' },
    { category: 'ledgers', slug: 'pehla-khata-banayein', title: 'Apna Pehla Khata Banayein' },
  ];

  // Search functionality - search in titles and summaries
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase();
    const allArticles = getAllArticles();

    return allArticles.filter(article => {
      const titleMatch = article.title.toLowerCase().includes(query);
      const summaryMatch = article.summary?.toLowerCase().includes(query);
      return titleMatch || summaryMatch;
    }).slice(0, 8); // Limit to 8 results
  }, [searchQuery]);

  const showSearchResults = searchQuery.length >= 2 && searchResults.length > 0;
  const showNoResults = searchQuery.length >= 2 && searchResults.length === 0;

  const content = (
    <div className="help-container">
      <div className="help-header help-header-home">
        <div className="help-header-icon">📖</div>
        <h1>Help Center</h1>
        <p className="help-subtitle">
          Hisab OK Hai kaise use karein? Yahan se seekhein.
        </p>
        <p className="help-subtitle-secondary">
          Simple Hindi mein - bilkul aasan
        </p>
      </div>

      {/* Search */}
      <div className="help-search-section">
        <input
          type="text"
          className="help-search-input"
          placeholder="Kuch search karein... (jaise 'budget', 'expense')"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Search Results */}
        {showSearchResults && (
          <div className="help-search-results">
            <p className="help-search-results-count">{searchResults.length} results mile</p>
            {searchResults.map((article) => {
              const category = getCategoryById(article.categoryId);
              return (
                <Link
                  key={`${article.categoryId}-${article.slug}`}
                  to={`/help/${article.categoryId}/${article.slug}`}
                  className="help-search-result-item"
                >
                  <span className="help-search-result-title">{article.title}</span>
                  <span className="help-search-result-category">
                    {category?.icon} {category?.name}
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {showNoResults && (
          <div className="help-search-no-results">
            <p>"{searchQuery}" ke liye kuch nahi mila</p>
            <p>Kuch aur search karke dekhein</p>
          </div>
        )}
      </div>

      {/* Popular Articles */}
      <div className="help-popular-section">
        <h3 className="help-section-title">Sabse Zyada Padhe Jaane Wale</h3>
        <div className="help-popular-links">
          {popularArticles.map((article) => (
            <Link
              key={`${article.category}-${article.slug}`}
              to={`/help/${article.category}/${article.slug}`}
              className="help-popular-link"
            >
              {article.title} →
            </Link>
          ))}
        </div>
      </div>

      {/* Categories Grid */}
      <h3 className="help-section-title">Categories</h3>
      <div className="help-categories-grid">
        {HELP_CATEGORIES.map((category) => {
          const articleCount = getArticlesByCategory(category.id).length;
          return (
            <Link
              key={category.id}
              to={`/help/${category.id}`}
              className="help-category-card"
            >
              <span className="help-category-icon">{category.icon}</span>
              <h2 className="help-category-name">{category.name}</h2>
              <p className="help-category-name-en">{category.nameEn}</p>
              <p className="help-category-description">{category.description}</p>
              {articleCount > 0 && (
                <span className="help-category-count">{articleCount} articles</span>
              )}
            </Link>
          );
        })}
      </div>

      <div className="help-footer">
        <p>
          Naye ho? <Link to="/help/getting-started">Shuru se padhein</Link> - step by step guide
        </p>
      </div>
    </div>
  );

  if (isAuthenticated) {
    return <Layout>{content}</Layout>;
  }

  return (
    <>
      <LandingNavbar />
      {content}
      <LandingFooter />
    </>
  );
};

export default HelpHome;
